// Config-driven text-analysis / transform engine. One component + one config
// table powers a cluster of pure-string tools (counting, analysis, reshaping).
// Everything runs locally in the browser.

export type TextToolkitVariant =
  | 'word-counter'
  | 'character-counter'
  | 'reading-time-calculator'
  | 'word-frequency-counter'
  | 'reverse-text'
  | 'remove-line-breaks'
  | 'palindrome-checker'
  | 'comma-separator'
  | 'find-unique-list-items'
  | 'find-duplicate-list-items'
  | 'reverse-list'
  | 'number-list-items'
  | 'remove-duplicate-words'
  | 'capitalize-words'
  | 'extract-emails'
  | 'extract-urls'
  | 'sentence-case-converter'
  | 'alternating-case-converter'
  | 'extract-numbers'
  | 'remove-punctuation'
  | 'text-sorter'
  | 'text-to-slug'
  | 'shuffle-list'
  | 'change-list-separator'
  | 'morse-code-translator';

export type TextToolStat = { label: string; value: string };

export type TextToolResult = {
  stats?: TextToolStat[];
  output?: string;
};

export type TextToolMeta = {
  key: TextToolkitVariant;
  title: string;
  badge: string;
  inputLabel: string;
  outputLabel: string;
  sample: string;
  kind: 'stats' | 'output';
};

export const textToolkitMeta: Record<TextToolkitVariant, TextToolMeta> = {
  'word-counter': {
    key: 'word-counter',
    title: 'Word Counter',
    badge: 'Count',
    inputLabel: 'Your text',
    outputLabel: 'Counts',
    sample: 'Paste or type your text here to count words, characters, sentences, and paragraphs.',
    kind: 'stats',
  },
  'character-counter': {
    key: 'character-counter',
    title: 'Character Counter',
    badge: 'Count',
    inputLabel: 'Your text',
    outputLabel: 'Counts',
    sample: 'Type here to count characters with and without spaces.',
    kind: 'stats',
  },
  'reading-time-calculator': {
    key: 'reading-time-calculator',
    title: 'Reading Time Calculator',
    badge: 'Time',
    inputLabel: 'Your text',
    outputLabel: 'Estimates',
    sample: 'Paste an article to estimate reading and speaking time.',
    kind: 'stats',
  },
  'word-frequency-counter': {
    key: 'word-frequency-counter',
    title: 'Word Frequency Counter',
    badge: 'Frequency',
    inputLabel: 'Your text',
    outputLabel: 'Word frequency (most common first)',
    sample: 'Paste text to see which words appear most often.',
    kind: 'output',
  },
  'reverse-text': {
    key: 'reverse-text',
    title: 'Reverse Text',
    badge: 'Reverse',
    inputLabel: 'Your text',
    outputLabel: 'Reversed text',
    sample: 'Hello world',
    kind: 'output',
  },
  'remove-line-breaks': {
    key: 'remove-line-breaks',
    title: 'Remove Line Breaks',
    badge: 'Clean',
    inputLabel: 'Your text',
    outputLabel: 'Single-line text',
    sample: 'Line one\nLine two\nLine three',
    kind: 'output',
  },
  'palindrome-checker': {
    key: 'palindrome-checker',
    title: 'Palindrome Checker',
    badge: 'Check',
    inputLabel: 'Word or phrase',
    outputLabel: 'Result',
    sample: 'A man a plan a canal Panama',
    kind: 'stats',
  },
  'comma-separator': {
    key: 'comma-separator',
    title: 'Comma Separator',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Comma-separated',
    sample: 'apple\nbanana\ncherry',
    kind: 'output',
  },
  'find-unique-list-items': {
    key: 'find-unique-list-items',
    title: 'Find Unique List Items',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Unique items',
    sample: 'apple\nbanana\napple\ncherry\nbanana',
    kind: 'output',
  },
  'find-duplicate-list-items': {
    key: 'find-duplicate-list-items',
    title: 'Find Duplicate List Items',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Duplicate items',
    sample: 'apple\nbanana\napple\ncherry\nbanana',
    kind: 'output',
  },
  'reverse-list': {
    key: 'reverse-list',
    title: 'Reverse List',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Reversed order',
    sample: 'first\nsecond\nthird',
    kind: 'output',
  },
  'number-list-items': {
    key: 'number-list-items',
    title: 'Number List Items',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Numbered list',
    sample: 'apple\nbanana\ncherry',
    kind: 'output',
  },
  'remove-duplicate-words': {
    key: 'remove-duplicate-words',
    title: 'Remove Duplicate Words',
    badge: 'Text',
    inputLabel: 'Your text',
    outputLabel: 'Without duplicate words',
    sample: 'the quick brown the fox quick',
    kind: 'output',
  },
  'capitalize-words': {
    key: 'capitalize-words',
    title: 'Capitalize Each Word',
    badge: 'Case',
    inputLabel: 'Your text',
    outputLabel: 'Title case',
    sample: 'the quick brown fox',
    kind: 'output',
  },
  'extract-emails': {
    key: 'extract-emails',
    title: 'Extract Emails',
    badge: 'Extract',
    inputLabel: 'Text containing emails',
    outputLabel: 'Emails found',
    sample: 'Contact ada@example.com or grace@test.dev for details.',
    kind: 'output',
  },
  'extract-urls': {
    key: 'extract-urls',
    title: 'Extract URLs',
    badge: 'Extract',
    inputLabel: 'Text containing URLs',
    outputLabel: 'URLs found',
    sample: 'Visit https://example.com and http://test.dev/page for more.',
    kind: 'output',
  },
  'sentence-case-converter': {
    key: 'sentence-case-converter',
    title: 'Sentence Case Converter',
    badge: 'Case',
    inputLabel: 'Your text',
    outputLabel: 'Sentence case',
    sample: 'this is a sentence. and here is another one.',
    kind: 'output',
  },
  'alternating-case-converter': {
    key: 'alternating-case-converter',
    title: 'Alternating Case Converter',
    badge: 'Case',
    inputLabel: 'Your text',
    outputLabel: 'aLtErNaTiNg CaSe',
    sample: 'Alternating case text',
    kind: 'output',
  },
  'extract-numbers': {
    key: 'extract-numbers',
    title: 'Extract Numbers',
    badge: 'Extract',
    inputLabel: 'Text containing numbers',
    outputLabel: 'Numbers found',
    sample: 'Order 12 items for $34.50 by 2026.',
    kind: 'output',
  },
  'remove-punctuation': {
    key: 'remove-punctuation',
    title: 'Remove Punctuation',
    badge: 'Clean',
    inputLabel: 'Your text',
    outputLabel: 'Without punctuation',
    sample: 'Hello, world! How are you?',
    kind: 'output',
  },
  'text-sorter': {
    key: 'text-sorter',
    title: 'Text Sorter',
    badge: 'Sort',
    inputLabel: 'Lines to sort',
    outputLabel: 'Sorted lines',
    sample: 'banana\napple\ncherry\napple',
    kind: 'output',
  },
  'text-to-slug': {
    key: 'text-to-slug',
    title: 'Text to Slug',
    badge: 'Slug',
    inputLabel: 'Your text',
    outputLabel: 'URL slug',
    sample: 'Hello World! This is My Post',
    kind: 'output',
  },
  'shuffle-list': {
    key: 'shuffle-list',
    title: 'Shuffle List',
    badge: 'List',
    inputLabel: 'One item per line',
    outputLabel: 'Shuffled list',
    sample: 'apple\nbanana\ncherry\ndate\nelderberry',
    kind: 'output',
  },
  'change-list-separator': {
    key: 'change-list-separator',
    title: 'Change List Separator',
    badge: 'List',
    inputLabel: 'List (new lines or commas)',
    outputLabel: 'Toggled separator',
    sample: 'apple\nbanana\ncherry',
    kind: 'output',
  },
  'morse-code-translator': {
    key: 'morse-code-translator',
    title: 'Morse Code Translator',
    badge: 'Morse',
    inputLabel: 'Text or Morse code',
    outputLabel: 'Translation',
    sample: 'SOS',
    kind: 'output',
  },
};

export function getTextToolkitMeta(variant: TextToolkitVariant): TextToolMeta {
  return textToolkitMeta[variant];
}

function countWords(text: string): number {
  const matches = text.trim().match(/\S+/g);
  return matches ? matches.length : 0;
}

const MORSE_MAP: Record<string, string> = {
  A: '.-',
  B: '-...',
  C: '-.-.',
  D: '-..',
  E: '.',
  F: '..-.',
  G: '--.',
  H: '....',
  I: '..',
  J: '.---',
  K: '-.-',
  L: '.-..',
  M: '--',
  N: '-.',
  O: '---',
  P: '.--.',
  Q: '--.-',
  R: '.-.',
  S: '...',
  T: '-',
  U: '..-',
  V: '...-',
  W: '.--',
  X: '-..-',
  Y: '-.--',
  Z: '--..',
  '0': '-----',
  '1': '.----',
  '2': '..---',
  '3': '...--',
  '4': '....-',
  '5': '.....',
  '6': '-....',
  '7': '--...',
  '8': '---..',
  '9': '----.',
  '.': '.-.-.-',
  ',': '--..--',
  '?': '..--..',
  "'": '.----.',
  '!': '-.-.--',
  '/': '-..-.',
  '(': '-.--.',
  ')': '-.--.-',
  '&': '.-...',
  ':': '---...',
  ';': '-.-.-.',
  '=': '-...-',
  '+': '.-.-.',
  '-': '-....-',
  _: '..--.-',
  '"': '.-..-.',
  $: '...-..-',
  '@': '.--.-.',
};

const MORSE_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(MORSE_MAP).map(([char, code]) => [code, char]),
);

function translateMorse(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  // If it looks like Morse (only dots, dashes, spaces, slashes), decode it.
  if (/^[.\-/\s|]+$/.test(trimmed)) {
    return trimmed
      .replace(/\|/g, '/')
      .split('/')
      .map((word) =>
        word
          .trim()
          .split(/\s+/)
          .map((code) => MORSE_REVERSE[code] ?? '')
          .join(''),
      )
      .join(' ')
      .trim();
  }
  // Otherwise encode text to Morse.
  return trimmed
    .toUpperCase()
    .split(/\s+/)
    .map((word) =>
      [...word]
        .map((char) => MORSE_MAP[char] ?? '')
        .filter(Boolean)
        .join(' '),
    )
    .join(' / ');
}

function formatMinutes(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0 sec';
  const minutes = Math.floor(totalMinutes);
  const seconds = Math.round((totalMinutes - minutes) * 60);
  if (minutes === 0) return `${seconds} sec`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
}

export function runTextTool(variant: TextToolkitVariant, input: string): TextToolResult {
  switch (variant) {
    case 'word-counter': {
      const words = countWords(input);
      const characters = input.length;
      const charactersNoSpaces = input.replace(/\s/g, '').length;
      const sentences = (input.match(/[^.!?]+[.!?]+/g) ?? []).length;
      const paragraphs = input
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean).length;
      const lines = input === '' ? 0 : input.split(/\n/).length;
      return {
        stats: [
          { label: 'Words', value: String(words) },
          { label: 'Characters', value: String(characters) },
          { label: 'Characters (no spaces)', value: String(charactersNoSpaces) },
          { label: 'Sentences', value: String(sentences) },
          { label: 'Paragraphs', value: String(paragraphs) },
          { label: 'Lines', value: String(lines) },
        ],
      };
    }
    case 'character-counter': {
      return {
        stats: [
          { label: 'Characters', value: String(input.length) },
          { label: 'Characters (no spaces)', value: String(input.replace(/\s/g, '').length) },
          { label: 'Words', value: String(countWords(input)) },
          { label: 'Lines', value: String(input === '' ? 0 : input.split(/\n/).length) },
        ],
      };
    }
    case 'reading-time-calculator': {
      const words = countWords(input);
      return {
        stats: [
          { label: 'Words', value: String(words) },
          { label: 'Reading time (225 wpm)', value: formatMinutes(words / 225) },
          { label: 'Speaking time (130 wpm)', value: formatMinutes(words / 130) },
        ],
      };
    }
    case 'word-frequency-counter': {
      const words = input.toLowerCase().match(/[\p{L}\p{N}']+/gu) ?? [];
      const counts = new Map<string, number>();
      for (const word of words) counts.set(word, (counts.get(word) ?? 0) + 1);
      const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      const output = sorted.map(([word, count]) => `${count}\t${word}`).join('\n');
      return { output };
    }
    case 'reverse-text': {
      return { output: [...input].reverse().join('') };
    }
    case 'remove-line-breaks': {
      return {
        output: input
          .replace(/\s*\n\s*/g, ' ')
          .replace(/[ \t]{2,}/g, ' ')
          .trim(),
      };
    }
    case 'palindrome-checker': {
      const normalized = input.toLowerCase().replace(/[^a-z0-9]/g, '');
      const reversed = [...normalized].reverse().join('');
      const isPalindrome = normalized.length > 0 && normalized === reversed;
      return {
        stats: [
          {
            label: 'Is a palindrome',
            value: normalized.length === 0 ? 'Enter text' : isPalindrome ? 'Yes' : 'No',
          },
          { label: 'Compared characters', value: String(normalized.length) },
        ],
      };
    }
    case 'comma-separator': {
      const items = input
        .split(/\n/)
        .map((item) => item.trim())
        .filter(Boolean);
      return { output: items.join(', ') };
    }
    case 'find-unique-list-items': {
      const seen = new Set<string>();
      const unique: string[] = [];
      for (const line of input.split(/\n/)) {
        const key = line.trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        unique.push(key);
      }
      return { output: unique.join('\n') };
    }
    case 'find-duplicate-list-items': {
      const counts = new Map<string, number>();
      for (const line of input.split(/\n/)) {
        const key = line.trim();
        if (!key) continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      const duplicates = [...counts.entries()]
        .filter(([, count]) => count > 1)
        .map(([item, count]) => `${item} (${count})`);
      return { output: duplicates.join('\n') };
    }
    case 'reverse-list': {
      const lines = input.split(/\n/).filter((line) => line.trim() !== '');
      return { output: lines.reverse().join('\n') };
    }
    case 'number-list-items': {
      const lines = input.split(/\n/).filter((line) => line.trim() !== '');
      return { output: lines.map((line, index) => `${index + 1}. ${line.trim()}`).join('\n') };
    }
    case 'remove-duplicate-words': {
      const seen = new Set<string>();
      const result = input.split(/(\s+)/).filter((token) => {
        if (/^\s+$/.test(token) || token === '') return true;
        const key = token.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return {
        output: result
          .join('')
          .replace(/\s{2,}/g, ' ')
          .trim(),
      };
    }
    case 'capitalize-words': {
      return {
        // A word boundary is ASCII-based, so it fails before a letter like e-acute and the
        // capitalization lands on the second letter. Match the first letter after a non-letter.
        output: input.replace(
          /(^|[^\p{L}\p{N}'])(\p{L})/gu,
          (_, before, letter) => before + letter.toUpperCase(),
        ),
      };
    }
    case 'extract-emails': {
      const matches = input.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) ?? [];
      return { output: [...new Set(matches)].join('\n') };
    }
    case 'extract-urls': {
      const raw = input.match(/https?:\/\/[^\s"'<>]+/g) ?? [];
      const matches = raw.map((url) => {
        let value = url;
        // Trailing sentence punctuation is not part of the URL. A closing bracket is,
        // but only when the URL opened one (Wikipedia disambiguation links).
        for (;;) {
          const last = value[value.length - 1];
          if (last && '.,;:!?'.includes(last)) {
            value = value.slice(0, -1);
            continue;
          }
          if (
            last === ')' &&
            (value.match(/\)/g) ?? []).length > (value.match(/\(/g) ?? []).length
          ) {
            value = value.slice(0, -1);
            continue;
          }
          break;
        }
        return value;
      });
      return { output: [...new Set(matches)].join('\n') };
    }
    case 'sentence-case-converter': {
      const lowered = input.toLowerCase();
      return {
        output: lowered.replace(/(^\s*\p{L})|([.!?]\s+\p{L})/gu, (match) => match.toUpperCase()),
      };
    }
    case 'alternating-case-converter': {
      // The first letter is lowercase, which is what the aLtErNaTiNg label promises.
      let upper = true;
      return {
        output: [...input]
          .map((char) => {
            if (!/\p{L}/u.test(char)) return char;
            upper = !upper;
            return upper ? char.toUpperCase() : char.toLowerCase();
          })
          .join(''),
      };
    }
    case 'extract-numbers': {
      // A minus sign only counts when it starts the number, so "555-1234" is two numbers
      // and not one negative. Thousands separators are kept, a trailing comma is not.
      const core = /\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?/g;
      const matches: string[] = [];
      for (const match of input.matchAll(core)) {
        const start = match.index ?? 0;
        const signed =
          start > 0 &&
          input[start - 1] === '-' &&
          (start === 1 || /[\s(]/.test(input[start - 2] ?? ' '));
        matches.push(signed ? `-${match[0]}` : match[0]);
      }
      return { output: matches.join('\n') };
    }
    case 'remove-punctuation': {
      return {
        output: input
          .replace(/[^\p{L}\p{N}\s]/gu, '')
          .replace(/\s{2,}/g, ' ')
          .trim(),
      };
    }
    case 'text-sorter': {
      const sorted = input
        .split('\n')
        .filter((line) => line.trim().length > 0)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
      return { output: sorted.join('\n') };
    }
    case 'text-to-slug': {
      // These letters have no decomposed form, so NFKD leaves them and the a-z filter
      // deletes them. Map them to their conventional Latin spelling first.
      const LATIN_EXPANSIONS: Record<string, string> = {
        ß: 'ss',
        æ: 'ae',
        œ: 'oe',
        ø: 'o',
        å: 'a',
        đ: 'd',
        ð: 'd',
        þ: 'th',
        ł: 'l',
        ı: 'i',
      };
      const slug = input
        .toLowerCase()
        .replace(/[ßæœøåđðþłı]/g, (char) => LATIN_EXPANSIONS[char] ?? char)
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return { output: slug };
    }
    case 'shuffle-list': {
      const lines = input.split(/\n/).filter((line) => line.trim() !== '');
      for (let i = lines.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [lines[i], lines[j]] = [lines[j], lines[i]];
      }
      return { output: lines.join('\n') };
    }
    case 'change-list-separator': {
      // Toggle between one-per-line and comma-separated based on the input.
      if (input.includes('\n')) {
        const items = input
          .split(/\n/)
          .map((item) => item.trim())
          .filter(Boolean);
        return { output: items.join(', ') };
      }
      const items = input
        .split(/,/)
        .map((item) => item.trim())
        .filter(Boolean);
      return { output: items.join('\n') };
    }
    case 'morse-code-translator': {
      return { output: translateMorse(input) };
    }
  }
}
